import React from 'react';
import { BarChart, Trash2, Layers, Heart } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных профилей
 */
const ComparisonSidebar = ({ 
  savedProfiles, 
  deleteSavedProfile, 
  loadSavedProfile 
}) => {
  // Если нет сохраненных профилей
  if (!savedProfiles || savedProfiles.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-red-500" />
          Сравнение профилей
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные профили для сравнения зон пульса.
          Рассчитайте свои зоны и сохраните результат.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-red-500" />
          Сравнение профилей
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedProfiles.length} {pluralize(savedProfiles.length, 'профиль', 'профиля', 'профилей')}
        </span>
      </div>

      <div className="space-y-4">
        {savedProfiles.map((profile) => (
          <div 
            key={profile.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedProfile(profile.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedProfile(profile.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Макс. пульс:</span>
                <span className="font-medium">
                  {profile.results.maxHeartRate} уд/мин
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Целевая зона:</span>
                <span>
                  {profile.results.targetZone.lowerHR}-{profile.results.targetZone.upperHR} уд/мин
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип:</span>
                <span className="text-red-600 dark:text-red-400">
                  {profile.results.exerciseType}
                </span>
              </div>
              
              {/* Визуализация зон в миниатюре */}
              <div className="mt-3 h-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                {profile.results.zones.map((zone, index) => {
                  const zoneColors = [
                    'bg-green-500', 'bg-blue-500', 'bg-yellow-500', 
                    'bg-orange-500', 'bg-red-500'
                  ];
                  
                  // Расчет ширины и позиции зоны пропорционально максимальному пульсу
                  const maxHR = profile.results.maxHeartRate;
                  const start = zone.lowerPercent / 100;
                  const width = (zone.upperPercent - zone.lowerPercent) / 100;
                  
                  return (
                    <div 
                      key={index}
                      className={`absolute h-full ${zoneColors[index]}`}
                      style={{
                        left: `${start * 100}%`,
                        width: `${width * 100}%`,
                        opacity: isTargetZone(zone, profile.results.targetZone) ? '1' : '0.7'
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {savedProfiles.length > 1 && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <Heart className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-red-600 dark:text-red-400">
              <p>Сравнивая профили, обратите внимание на разницу в целевых зонах для разных типов тренировок и состояний здоровья.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для проверки, является ли зона целевой
const isTargetZone = (zone, targetZone) => {
  return (
    zone.lowerPercent <= targetZone.lowerPercent && 
    zone.upperPercent >= targetZone.upperPercent
  ) || (
    zone.lowerPercent >= targetZone.lowerPercent && 
    zone.lowerPercent <= targetZone.upperPercent
  ) || (
    zone.upperPercent >= targetZone.lowerPercent && 
    zone.upperPercent <= targetZone.upperPercent
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default ComparisonSidebar;